import debounce from 'lodash/debounce';
import Cookie from 'js-cookie';

function debug(...args) {
  if (window.matrixDebug !== true) return;
  console.debug('[MatrixIdle]', ...args);
}

/**
 * The page idle time manager to trigger auto logout once exceed maximum idle time
 */
class IdleTimeManager {
  static IDLE_EVENTS = ['mousedown', 'mouseover', 'mousemove', 'keydown', 'wheel', 'touchstart'];

  constructor() {
    this.isFedRAMP = false;
    this.pageIdleTimer = undefined;
    this.enabled = false;

    IdleTimeManager.IDLE_EVENTS.forEach(event =>
      window.addEventListener(event, this.updateLastActionTime, true)
    );
  }

  /**
   * Timer key to save the latest action time.
   * Isolate the FedRamp with other Commercial envs
   */
  get cookieKey() {
    return this.isFedRAMP === true ? '__IDLE_FEDRAMP__' : '__IDLE_COMMERCIAL__';
  }

  /**
   * The attributes to set the cookie
   */
  get cookieConfig() {
    // Should be either .autodesk.eu or .autodesk.com
    const domain = window.location.hostname
      .split('.')
      .slice(-2)
      .join('.');
    return {
      domain: `.${domain}`,
      path: '/',
      secure: true,
      sameSite: 'strict',
      expires: 30
    };
  }

  /**
   * Get the maximum idle time in milliseconds based on isFedRAMP or not.
   * Use given idleTimeInMs if provided, otherwise use default value.
   */
  get idleThresholdInMS() {
    if (this.idleTimeInMs && typeof this.idleTimeInMs === 'number') return this.idleTimeInMs;
    return this.isFedRAMP === true ? 30 * 60 * 1000 : 2 * 60 * 60 * 1000;
  }

  /**
   * Check if the allowed Idle time is expired.
   * @returns {boolean} true if idle time is expired or the cookie doesn't exist
   */
  get isExpired() {
    const updateTime = Cookie.get(this.cookieKey);
    if (!updateTime || isNaN(updateTime)) {
      return true;
    }
    const idleTime = Date.now() - Number(updateTime);
    const expired = idleTime > this.idleThresholdInMS;
    if (expired) {
      console.warn(`[MatrixIdle] idle expired <${idleTime}> / <${this.idleThresholdInMS}>!`);
    }
    return expired;
  }

  clearTimer() {
    // Clear all required info
    if (this.pageIdleTimer) {
      window.clearInterval(this.pageIdleTimer);
      this.pageIdleTimer = null; // Avoid reset timer on logout process
      debug('existing timer cleared');
    }
    Cookie.remove(this.cookieKey, this.cookieConfig);
    debug('cookie removed');
  }

  /**
   * Setup an interval timer to check the page idle time periodically.
   * Trigger logout if exceed the maximum idle time.
   */
  setupPageIdleTimer() {
    this.pageIdleTimer = window.setInterval(async () => {
      if (!this.enabled) return; // Skip if idle time manager is disabled
      try {
        if (this.isExpired) {
          this.clearTimer();
          console.warn(`[MatrixIdle] idle expired!`);
          // Trigger logout
          if (this.onSignOut) this.onSignOut();
        } else {
          const idleTime = Date.now() - Number(Cookie.get(this.cookieKey));
          debug(`checked at ${new Date().toISOString()}:`, idleTime);
        }
      } catch (ex) {
        console.error(`[MatrixIdle] failed to check idle time!`, ex);
      }
    }, 2 * 1000);
    debug('timer set up');
  }

  /**
   * Update the last action time to cookie.
   *
   * @param {String} source The event source to update the last action time
   */
  setupActionTime(source) {
    try {
      Cookie.set(this.cookieKey, Date.now(), this.cookieConfig);
      debug(`updated at ${new Date().toISOString()}:`, source);
    } catch (error) {
      console.error('[MatrixIdle] error on updating last action time', error);
    }
  }

  /**
   * Update the last action time to local storage.
   *
   * Need to ensure the idle logic is enabled first,
   * consumer may manually update the last action time besides the idle events to ensure page not idle logout.
   * E.g. the time consuming actions: uploading / downloading files
   */
  updateLastActionTime = debounce(
    (event = { type: 'manual' }) => this.setupActionTime(event.type),
    10 * 1000,
    { leading: true, maxWait: 15 * 1000 }
  );

  /**
   * Update the isFedRAMP, idleTimeInMs, onSignOut callback
   */
  update(props) {
    if (props.onSignOut !== undefined) this.onSignOut = props.onSignOut;
    if (props.idleTimeInMs !== undefined) this.idleTimeInMs = props.idleTimeInMs;
    if (props.isFedRAMP !== undefined) {
      const changed = this.isFedRAMP !== props.isFedRAMP;
      // Ensure reset isFedRAMP before calling this.toggle
      this.isFedRAMP = props.isFedRAMP;
      // Once region is changed, call toggle to re-initialize the idle timestamp
      if (changed) this.toggle(this.enabled);
    }

    debug(`update idle time manager with`, props, ` to `, {
      isFedRAMP: this.isFedRAMP,
      idleTimeInMs: this.idleTimeInMs,
      onSignOut: this.onSignOut
    });
  }

  /**
   * Toggle the idle time manager
   */
  toggle(enable) {
    this.enabled = enable === true;
    debug(`idle time toggle, enabled?`, this.enabled);
    if (this.enabled === true) {
      // When the cookie exists and the idle time has expired,
      // we leave the cookie as is and let the timer perform a logout.
      if (Cookie.get(this.cookieKey) && this.isExpired) {
        debug(
          'Cookie exists and contains expired data, skipping initialization and letting the timer handle it.'
        );
      } else {
        // Ensure the last action time is updated before the timer starts.
        // Don't call updateLastActionTime directly since it's debounced.
        this.setupActionTime('init');
      }

      // Setup the page idle timer if not existed
      if (this.pageIdleTimer === undefined) this.setupPageIdleTimer();
    }
  }
}

export default IdleTimeManager;
